import {
  Button,
  Field,
  Input,
  RichTextarea,
  Select,
  Textarea,
} from '@payaca/ambrosia';
import ValidatedForm, {
  TRenderFormContents,
} from '@payaca/components/validatedForm/ValidatedForm';
import { FC, useCallback, useMemo, type JSX } from 'react';

import { useGetProjectCustomFieldVariables } from '@/hooks/useProjectCustomFields';
import { useAccount } from '@/utils/storeHooks';

import {
  getAllowEmptyValidator,
  getIsRequiredFieldValidator,
  getLengthFieldValidator,
  getNumericalRangeFieldValidator,
} from '@payaca/helpers/fieldValidationHelper';
import {
  EditablePaymentSchedule,
  getPaymentScheduleFieldValidator,
  isPaymentScheduleValid,
} from '@payaca/helpers/paymentScheduleHelper';
import {
  EntityTemplatePlaceholderVariables,
  PublicEntityTemplate,
} from '@payaca/types/entity-templates';
import { isNullish } from '@payaca/utilities/guards';
import CreatePaymentScheduleControl from './CreatePaymentScheduleControl';

type ProposalTemplate = Omit<PublicEntityTemplate<'proposal'>, 'publicId'>;

/**
 * Form-bound shape of a proposal template. Differs from the persisted shape
 * because Ambrosia `Input` emits strings from `e.target.value`, so any
 * number-typed field the user has touched is actually a string in form
 * state until we coerce on save.
 */
type ProposalTemplateFormState = Omit<ProposalTemplate, 'data'> & {
  data: Omit<ProposalTemplate['data'], 'paymentSchedule' | 'validForDays'> & {
    paymentSchedule?: EditablePaymentSchedule | null;
    validForDays?: number | string | null;
  };
};

type Props = {
  proposalTemplate?: Omit<PublicEntityTemplate<'proposal'>, 'publicId'>;
  onRequestSave?: (
    proposalTemplate: Omit<PublicEntityTemplate<'proposal'>, 'publicId'>
  ) => void;
  allowModifyTemplateNameDescription?: boolean;
};
const CreateEditProposalTemplateControl: FC<Props> = ({
  proposalTemplate,
  onRequestSave,
  allowModifyTemplateNameDescription = true,
}: Props): JSX.Element => {
  const projectCustomFieldVariables =
    useGetProjectCustomFieldVariables<EntityTemplatePlaceholderVariables>(
      EntityTemplatePlaceholderVariables.PROJECT_CUSTOMFIELD_VALUE,
      'Project custom field'
    );

  const editorVariables = useMemo(() => {
    const ev: {
      name: string;
      displayName: string;
    }[] = [
      {
        name: EntityTemplatePlaceholderVariables.CUSTOMER_NAME,
        displayName: 'Customer name',
      },
      {
        name: EntityTemplatePlaceholderVariables.CUSTOMER_PRIMARYCONTACT_NAME,
        displayName: 'Primary contact name',
      },
      {
        name: EntityTemplatePlaceholderVariables.CUSTOMER_PRIMARYCONTACT_FIRSTNAME,
        displayName: 'Primary contact first name',
      },
      {
        name: EntityTemplatePlaceholderVariables.PROJECT_SITEADDRESSES_ARRAYSTRING,
        displayName: 'Project site address(es)',
      },
    ];

    projectCustomFieldVariables.forEach((variable) => {
      ev.push({
        name: variable.variableName,
        displayName: variable.displayName,
      });
    });

    return ev;
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'projectCustomFieldVariables'
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: projectCustomFieldVariables.length
  }, [projectCustomFieldVariables?.length]);

  const fieldValidators = useMemo(() => {
    return {
      name: [
        getIsRequiredFieldValidator(),
        getLengthFieldValidator({ min: 0, max: 60 }),
      ],
      description: [getLengthFieldValidator({ min: 0, max: 255 })],
      'data.validForDays': [
        getAllowEmptyValidator(
          getNumericalRangeFieldValidator(
            -0.01,
            undefined,
            undefined,
            'This field must be a positive number'
          )
        ),
      ],
      'data.paymentSchedule': [getPaymentScheduleFieldValidator()],
      'data.summaryMessages[0].message': [
        getLengthFieldValidator({ min: 0, max: 125 }),
      ],
    };
  }, []);

  const account = useAccount();

  const initialFormState = useMemo<ProposalTemplateFormState>(() => {
    return {
      name: proposalTemplate?.name ?? '',
      description: proposalTemplate?.description ?? '',
      entityType: 'proposal',
      data: {
        validForDays: account?.propositionValidForDays,
        paymentSchedule: null,
        ...proposalTemplate?.data,
        summaryMessages: proposalTemplate?.data.summaryMessages || [
          {
            level: 'alert',
            message: '',
          },
        ],
      },
    };
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'account.propositionValidForDays'
  }, [proposalTemplate]);

  const saveTemplate = (formState: ProposalTemplateFormState) => {
    const validStages = (formState.data.paymentSchedule?.stages || [])
      .map((x) => ({ ...x, percentageDue: Number(x.percentageDue) }))
      .filter((stage) => stage.description && stage.percentageDue);
    const cleanedPaymentSchedule = {
      explainer: validStages.length
        ? formState.data.paymentSchedule?.explainer
        : null,
      stages: validStages,
    };
    const paymentScheduleToSave = isPaymentScheduleValid(cleanedPaymentSchedule)
      ? cleanedPaymentSchedule
      : null;

    const templateToSave: Omit<PublicEntityTemplate<'proposal'>, 'publicId'> = {
      ...formState,
      data: {
        ...formState.data,
        paymentSchedule: paymentScheduleToSave,
        validForDays: isNullish(formState.data.validForDays)
          ? formState.data.validForDays
          : Number(formState.data.validForDays),
      },
    };

    onRequestSave?.(templateToSave);
  };

  const renderFormContents: TRenderFormContents<ProposalTemplateFormState> =
    useCallback(
      (
        isValid,
        formState,
        validationState,
        touchedState,
        onFieldChange,
        onFieldTouch
      ) => {
        return (
          <>
            {allowModifyTemplateNameDescription && (
              <>
                <Field.Legacy
                  name="name"
                  validationState={
                    !validationState?.['name']?.isValid &&
                    touchedState?.['name']
                      ? {
                          isValid: false,
                          validationMessages: validationState?.['name']?.errors,
                        }
                      : undefined
                  }
                >
                  <Field.Label>Template name</Field.Label>
                  <Input
                    value={formState.name ?? ''}
                    onChange={(value) => onFieldChange({ name: value })}
                    onBlur={() => onFieldTouch?.('name')}
                  />
                </Field.Legacy>
                <Field.Legacy
                  name="description"
                  validationState={
                    !validationState?.['description']?.isValid &&
                    touchedState?.['description']
                      ? {
                          isValid: false,
                          validationMessages:
                            validationState?.['description']?.errors,
                        }
                      : undefined
                  }
                >
                  <Field.Label>Template description</Field.Label>
                  <Textarea
                    value={formState.description ?? ''}
                    onChange={(value) => onFieldChange({ description: value })}
                    onBlur={() => onFieldTouch?.('description')}
                  />
                </Field.Legacy>
                <Field.Legacy name="data.type">
                  <Field.Label>Proposal type</Field.Label>
                  <Select
                    className={'max-w-[200px]'}
                    value={formState.data.type ?? undefined}
                    onChange={(value) =>
                      onFieldChange({ ['data.type']: value })
                    }
                    options={[
                      { value: 'ESTIMATE', label: 'Estimate' },
                      { value: 'QUOTE', label: 'Quote' },
                    ]}
                  />
                </Field.Legacy>
                <Field.Legacy name="data.introduction">
                  <Field.Label>Intro</Field.Label>
                  <RichTextarea
                    value={formState.data.introduction ?? null}
                    onChange={(value) =>
                      onFieldChange({ ['data.introduction']: value })
                    }
                    variables={editorVariables}
                  />
                </Field.Legacy>
                <Field.Legacy name="data.notes">
                  <Field.Label>Notes</Field.Label>
                  <RichTextarea
                    value={formState.data.notes ?? null}
                    onChange={(value) =>
                      onFieldChange({ ['data.notes']: value })
                    }
                    variables={editorVariables}
                  />
                </Field.Legacy>
                <Field.Legacy
                  name="data.validForDays"
                  validationState={
                    !validationState?.['data.validForDays']?.isValid &&
                    touchedState?.['data.validForDays']
                      ? {
                          isValid: false,
                          validationMessages:
                            validationState?.['data.validForDays']?.errors,
                        }
                      : undefined
                  }
                >
                  <Field.Label>Valid for (days)</Field.Label>

                  <Input
                    className={'max-w-[200px]'}
                    value={formState.data.validForDays ?? ''}
                    onChange={(value) =>
                      onFieldChange({ ['data.validForDays']: value })
                    }
                    onBlur={() => onFieldTouch?.('data.validForDays')}
                    type="number"
                    step={1}
                  />
                </Field.Legacy>

                <CreatePaymentScheduleControl
                  onChange={(change) =>
                    onFieldChange({ ['data.paymentSchedule']: change })
                  }
                  paymentSchedule={formState.data.paymentSchedule ?? undefined}
                  fieldValidationState={
                    validationState
                      ? {
                          ...validationState?.['data.paymentSchedule'],
                          validationMessages:
                            validationState?.['data.paymentSchedule']?.errors,
                        }
                      : undefined
                  }
                />
                <Field.Legacy
                  name="data.summaryMessages[0].message"
                  validationState={
                    !validationState?.['data.summaryMessages[0].message']
                      ?.isValid &&
                    touchedState?.['data.summaryMessages[0].message']
                      ? {
                          isValid: false,
                          validationMessages:
                            validationState?.['data.summaryMessages[0].message']
                              ?.errors,
                        }
                      : undefined
                  }
                >
                  <Field.Label>Payment summary alert</Field.Label>
                  <p className="mb-2">
                    This text will show immediately below the &#34;Total payable
                    amount&#34; in the summary box on the right.
                  </p>
                  <Input
                    value={formState.data.summaryMessages?.[0]?.message ?? ''}
                    onChange={(value) =>
                      onFieldChange({
                        ['data.summaryMessages[0].message']: value,
                      })
                    }
                    onBlur={() =>
                      onFieldTouch?.('data.summaryMessages[0].message')
                    }
                  />
                </Field.Legacy>
              </>
            )}
            <div className="actions-container">
              <Button
                onClick={() => saveTemplate(formState)}
                disabled={!isValid}
              >
                {proposalTemplate ? 'Save' : 'Create'} Proposal Template
              </Button>
            </div>
          </>
        );
      },
      // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'saveTemplate'
      // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: onRequestSave
      [
        proposalTemplate,
        onRequestSave,
        allowModifyTemplateNameDescription,
        editorVariables,
      ]
    );

  return (
    <div className="create-edit-scheduled-event-template-control">
      <ValidatedForm
        renderFormContents={renderFormContents}
        fieldValidators={fieldValidators}
        initialFormState={initialFormState}
      ></ValidatedForm>
    </div>
  );
};
export default CreateEditProposalTemplateControl;
